<template>
  <div class="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" @click.self="$emit('close')">
    <div class="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
      <div class="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
        <h3 class="text-sm font-semibold text-gray-800">
          {{ editing.id ? 'Edit Source Config' : 'New Source Config' }}
        </h3>
        <button @click="$emit('close')" class="text-gray-400 hover:text-gray-700">&times;</button>
      </div>

      <div class="p-5 space-y-4">
        <div>
          <label class="block text-[11px] font-medium text-gray-600 mb-1">Name</label>
          <input
            v-model="editing.name"
            type="text"
            class="w-full text-sm px-3 py-1.5 border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-dark"
            placeholder="e.g. 'Last 30d engagements for practice'"
            @input="dirty = true"
          />
        </div>

        <div>
          <label class="block text-[11px] font-medium text-gray-600 mb-1">Source Class</label>
          <select
            v-model="editing.source_class"
            class="w-full text-sm px-3 py-1.5 border border-gray-200 rounded bg-white focus:outline-none focus:border-gray-400"
            @change="onClassChange"
            :disabled="!!editing.id"
          >
            <option value="">— Pick a registered Source class —</option>
            <option v-for="cls in sourceClasses" :key="cls.name" :value="cls.name">
              {{ cls.name }}{{ cls.cacheable ? ' (cacheable)' : '' }}
            </option>
          </select>
          <p v-if="selectedClass" class="text-[10px] text-gray-500 mt-1">{{ selectedClass.description }}</p>
          <p v-if="editing.id" class="text-[10px] text-gray-400 mt-1">Class is fixed after creation. Duplicate to switch.</p>
        </div>

        <div>
          <label class="block text-[11px] font-medium text-gray-600 mb-1">Description</label>
          <textarea
            v-model="editing.description"
            rows="2"
            class="w-full text-sm px-3 py-1.5 border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-dark resize-none"
            placeholder="Optional — what this Source is for"
            @input="dirty = true"
          />
        </div>

        <!-- Custom params editor for known Source classes -->
        <div v-if="editing.source_class === 'DataAttributeSource'">
          <label class="block text-[11px] font-medium text-gray-600 mb-2">Params</label>
          <DataAttributeSourceParams
            :modelValue="editing.params"
            :delivery-mode="editing.delivery_mode"
            @update:modelValue="onCustomParamsUpdate"
          />
        </div>

        <!-- Generic JSON params editor (fallback) -->
        <div v-else>
          <label class="block text-[11px] font-medium text-gray-600 mb-1">
            Params (JSON)
            <span v-if="selectedClass" class="text-gray-400 font-normal">
              — schema: <code class="text-[10px]">{{ JSON.stringify(selectedClass.param_schema?.required || []) }}</code> required
            </span>
          </label>
          <textarea
            v-model="paramsStr"
            rows="8"
            spellcheck="false"
            class="w-full text-[11px] px-2 py-1.5 border border-gray-200 rounded font-mono bg-white focus:outline-none focus:border-gray-400 resize-vertical"
            placeholder='{ "limit": 5 }'
            @input="dirty = true; paramsError = null"
          />
          <p v-if="paramsError" class="text-[10px] text-red-500 mt-1">{{ paramsError }}</p>
          <p v-if="selectedClass" class="text-[10px] text-gray-400 mt-1">
            Skip <code>x-runtime</code> params (e.g. <code>entity_id</code>) — those are supplied per Run.
          </p>
        </div>

        <div class="flex items-center gap-2">
          <input
            id="src-required"
            type="checkbox"
            v-model="editing.required"
            @change="dirty = true"
          />
          <label for="src-required" class="text-xs text-gray-700">
            Required (fetch failure aborts the Run)
          </label>
        </div>

        <div v-if="selectedClass">
          <label class="block text-[11px] font-medium text-gray-600 mb-1">Delivery mode</label>
          <div v-if="supportedModes.length === 1" class="text-xs text-gray-500 px-2 py-1.5 bg-gray-50 border border-gray-200 rounded font-mono">
            {{ supportedModes[0] }}
            <span class="text-[10px] text-gray-400 ml-1">(only mode this class supports)</span>
          </div>
          <div v-else class="flex gap-2">
            <label
              v-for="mode in supportedModes"
              :key="mode"
              class="flex-1 px-3 py-2 border rounded cursor-pointer text-xs"
              :class="editing.delivery_mode === mode ? 'border-gray-900 bg-gray-50' : 'border-gray-200 hover:bg-gray-50'"
            >
              <input
                type="radio"
                :value="mode"
                v-model="editing.delivery_mode"
                @change="dirty = true"
                class="mr-2"
              />
              <span class="font-mono">{{ mode }}</span>
              <span class="block text-[10px] text-gray-500 mt-0.5">
                {{ mode === 'inline_data'
                  ? 'Rows in prompt. Works in any execution_mode.'
                  : 'Named CTE for the run_query tool. Requires execution_mode=agentic.'
                }}
              </span>
            </label>
          </div>
        </div>
      </div>

      <div class="px-5 py-3 border-t border-gray-200 flex items-center gap-2.5">
        <button
          v-if="editing.id"
          @click="runPreview"
          :disabled="previewing"
          class="text-xs px-3 py-1.5 rounded border border-gray-200 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40"
        >{{ previewing ? 'Previewing…' : 'Preview' }}</button>
        <span class="text-[11px] mr-auto" :class="dirty ? 'text-blue-600' : 'text-gray-400'">
          {{ dirty ? 'Unsaved changes' : 'Up to date' }}
        </span>
        <button @click="$emit('close')" class="text-xs px-3 py-1.5 rounded border border-gray-200 text-gray-600 bg-white hover:bg-gray-50">Cancel</button>
        <button @click="save" :disabled="!dirty || saving" class="text-xs px-3 py-1.5 rounded bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-40">
          {{ saving ? 'Saving…' : (editing.id ? 'Save' : 'Create') }}
        </button>
      </div>

      <div v-if="previewResult || previewError" class="px-5 py-3 border-t border-gray-200 bg-gray-50">
        <div class="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-2">Preview</div>
        <div v-if="previewError" class="text-xs text-red-500">{{ previewError }}</div>
        <div v-else class="text-[11px] font-mono whitespace-pre-wrap text-gray-700 max-h-64 overflow-y-auto">
          metadata: {{ JSON.stringify(previewResult.metadata, null, 2) }}

          data (truncated): {{ typeof previewResult.data_preview === 'string' ? previewResult.data_preview : JSON.stringify(previewResult.data_preview, null, 2) }}
        </div>
      </div>

      <div v-if="saveError" class="px-5 py-2 border-t border-gray-200 text-xs text-red-500">{{ saveError }}</div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import DataAttributeSourceParams from './DataAttributeSourceParams.vue'

const API_BASE = 'api/intelligence/'

const CUSTOM_PARAMS_CLASSES = new Set(['DataAttributeSource'])

export default {
  name: 'SourceConfigModal',
  components: { DataAttributeSourceParams },
  props: {
    initial: { type: Object, default: () => ({}) },
    sourceClasses: { type: Array, default: () => [] },
  },
  emits: ['close', 'saved'],

  data() {
    return {
      editing: {
        id: this.initial.id || null,
        name: this.initial.name || '',
        source_class: this.initial.source_class || '',
        description: this.initial.description || '',
        params: this.initial.params || {},
        required: this.initial.required ?? true,
        delivery_mode: this.initial.delivery_mode || 'inline_data',
      },
      paramsStr: JSON.stringify(this.initial.params || {}, null, 2),
      paramsError: null,
      saveError: null,
      saving: false,
      dirty: false,
      previewing: false,
      previewResult: null,
      previewError: null,
    }
  },

  computed: {
    selectedClass() {
      return this.sourceClasses.find(c => c.name === this.editing.source_class)
    },
    supportedModes() {
      return this.selectedClass?.supported_delivery_modes || ['inline_data']
    },
    usesCustomParams() {
      return CUSTOM_PARAMS_CLASSES.has(this.editing.source_class)
    },
  },

  watch: {
    'editing.source_class'() {
      // If the picked class doesn't support the current delivery_mode,
      // snap it back to a supported one.
      if (!this.supportedModes.includes(this.editing.delivery_mode)) {
        this.editing.delivery_mode = this.supportedModes[0] || 'inline_data'
      }
    },
  },

  methods: {
    onClassChange() {
      this.dirty = true
      if (this.editing.id) return
      // seed params from the class's param_schema defaults (skipping x-runtime keys).
      const schema = this.selectedClass?.param_schema || {}
      const seed = {}
      for (const [k, v] of Object.entries(schema.properties || {})) {
        if (v['x-runtime']) continue
        if (v.default !== undefined) seed[k] = v.default
      }
      if (this.usesCustomParams) {
        this.editing.params = seed
      } else {
        this.paramsStr = JSON.stringify(seed, null, 2)
      }
    },

    parseParams() {
      // Custom params editors write directly to editing.params — bypass paramsStr.
      if (this.usesCustomParams) {
        return this.editing.params || {}
      }
      try {
        return JSON.parse(this.paramsStr || '{}')
      } catch {
        this.paramsError = 'Invalid JSON'
        return null
      }
    },

    onCustomParamsUpdate(val) {
      this.editing.params = val
      this.dirty = true
    },

    async save() {
      this.saveError = null
      this.paramsError = null
      if (!this.editing.name) { this.saveError = 'Name required'; return }
      if (!this.editing.source_class) { this.saveError = 'Source class required'; return }
      const params = this.parseParams()
      if (params === null) return

      this.saving = true
      try {
        const payload = {
          name: this.editing.name,
          source_class: this.editing.source_class,
          description: this.editing.description || '',
          params,
          required: this.editing.required,
          delivery_mode: this.editing.delivery_mode || 'inline_data',
        }
        let data
        if (this.editing.id) {
          ({ data } = await axios.patch(`${API_BASE}source-configs/${this.editing.id}/`, payload))
        } else {
          ({ data } = await axios.post(`${API_BASE}source-configs/`, payload))
        }
        this.$emit('saved', data)
      } catch (e) {
        this.saveError = e.response?.data?.detail
          || JSON.stringify(e.response?.data || {})
          || 'Save failed'
      } finally {
        this.saving = false
      }
    },

    async runPreview() {
      this.previewing = true
      this.previewError = null
      this.previewResult = null
      try {
        const { data } = await axios.post(`${API_BASE}source-configs/${this.editing.id}/preview/`, {
          preview_inputs: {},
        })
        this.previewResult = data
      } catch (e) {
        this.previewError = e.response?.data?.detail
          || JSON.stringify(e.response?.data || {})
          || 'Preview failed'
      } finally {
        this.previewing = false
      }
    },
  },
}
</script>
